import {
  USUARIO_R2R_ADICIONAL,
  USUARIO_PRIMER_R2R_INCLUIDO,
  USUARIO_NO_LOGADO,
  USUARIO_SEGUNDO_R2R_INCLUIDO,
  USUARIO_R2R_ADICIONAL_CON_RENOVACION,
} from "@/constants/get-my-products";

import {
  URLSPURCHASEPROCESS,
  BUYPROCESS_URL_KEY_PARAMS,
  BUYPROCESS_URL_PARAM_PID
} from "@/constants/index";
import { toKebabCase } from "@/utils/common/to-kebap-case";
import { calculateTotalAmountR2R, parsePrice } from "../common/price";
import { goLoginCallbackUrl } from "../htmlUtils";
import { getFreeOfferPrice, normalizeProducts } from "../normalizers/devices-list";

/**
 * Función que recibe todas las ofertas, la capacidad y el color selecciondos, y te devulve la oferta específica
 * en base a los parametros seleccionados
 * @param {Object} offers
 * @param {string} capacity
 * @param {string} color
 * @returns Oferta seleccionada segun la capacidad y el color
 */
export const getOfferByCapacityColor = (offers, capacity, color) => {
  if (offers && capacity && offers[capacity]) {
    const mainOffer = offers[capacity];
    if (color && mainOffer) {
      return mainOffer[color];
    }
  }
  return offers[color];
};

/**
 * Función que recibe la lista con los terminales de crossSelling, y nos devuelve los productos que aplican al cliente
 * @param {Array} list
 * @returns Ofertas principales
 */
export const getCrossSellingMainDevices = (
  listCrossSelling,
  listCrossSellingRTR,
  listCrossSellingRTRAdditional,
  clientType,
  categoryFusionInfo,
  query,
  profiles,
  custoStamps,
  userDiscountNewModelR2R,
  financing
) => {
  let products = [];
  switch (clientType) {
    case USUARIO_R2R_ADICIONAL:
    case USUARIO_R2R_ADICIONAL_CON_RENOVACION:
      products = listCrossSellingRTRAdditional;
      break;
    case USUARIO_NO_LOGADO:
    case USUARIO_PRIMER_R2R_INCLUIDO:
    case USUARIO_SEGUNDO_R2R_INCLUIDO:
      //Si no hay crossSelling de R2R, se fuerza el de libre (aunque se ofrezcan terminales de menos precio)
      if (Array.isArray(listCrossSellingRTR) && listCrossSellingRTR.length > 0) {
        products = listCrossSellingRTR;
      } else {
        products = listCrossSelling;
      }
      break;
    default:
      products = listCrossSelling;
      break;
  }
  //const strictProducts = products.map(product => getProductWithStrictClientOffers(product, clientType));
  const normalizedProductCards = normalizeProducts({
    products: products,
    finalClientType: clientType,
    finalFusionCategoryId: categoryFusionInfo,
    queryParams: query,
    profiles,
    custoStamps,
    userDiscountNewModelR2R,
    userFinancing: financing,
  });
  return normalizedProductCards;
};

/*
  Filtra las ofertas para que solo haya una oferta por producto
*/
const getProductWithStrictClientOffers = (product, clientType) => {
  switch (clientType) {
    case USUARIO_R2R_ADICIONAL:
      return {
        ...product,
        offers: {
          r2rAdditional: product.offers.r2rAdditional
        }
      }
    case USUARIO_PRIMER_R2R_INCLUIDO:
    case USUARIO_SEGUNDO_R2R_INCLUIDO:
      return {
        ...product,
        offers: {
          r2r: product.offers.r2r
        }
      }
    default:
      return {
        ...product,
        offers: {
          libre: product.offers.libre,
          r2r: product.offers.r2r
        }
      };
  }
}

export const getRelatedDevices = ({ relatedDevices, clientType, categoryFusionInfo, query, profiles, custoStamps }) => {
  const strictProducts = relatedDevices?.products.map(product => getProductWithStrictClientOffers(product, clientType))
  const normalizedProductCards = normalizeProducts({
    products: strictProducts,
    finalClientType: clientType,
    finalFusionCategoryId: categoryFusionInfo,
    queryParams: query,
    profiles,
    custoStamps
  });
  return normalizedProductCards;
}
/**
 * Función que recibe un arrays de imagenes y devuelve la imagen principal
 * @param {Array} imagen
 * @returns Imagen principal
 */
export const getMainImageDevice = (imagen) => {
  if (imagen && Array.isArray(imagen) && imagen.length > 0 && imagen[0]) {
    return imagen[0];
  }
  return null;
};

const getMainPurchaseParams = (offerSelected) => {
  let mainPurchaseParams = "";
  if (offerSelected) {
    let { productOffering, productSpecs } = offerSelected;
    let { color, storage } = productSpecs;
    if (productOffering && color && storage) {
      const packPOs = productOffering.split(":");
      const packColors = color.split(":");
      const packStorages = storage.split(":");
      mainPurchaseParams = `${BUYPROCESS_URL_KEY_PARAMS.productOffering}=${packPOs[0]}&${BUYPROCESS_URL_KEY_PARAMS.productSpecStorage}=${packStorages[0]}&${BUYPROCESS_URL_KEY_PARAMS.productSpecColor}=${packColors[0]}`;
      // Es pack
      if (isPackOffer(offerSelected) && packPOs.length > 1) {
        let secondaryR2Rs = ""
        for (let i = 1; i < packPOs.length; i++) {
          secondaryR2Rs = `${secondaryR2Rs}${i > 1 ? ',' : ''}${packPOs[i]}_${packStorages[i]}_${packColors[i]}`
        }
        mainPurchaseParams = `${mainPurchaseParams}&${BUYPROCESS_URL_KEY_PARAMS.secondaryR2Rs}=${secondaryR2Rs}`
      }
    }
  }
  return mainPurchaseParams;
}

/**
 * La función recibe la oferta, y un booleano para saber si el terminal es R2R o libre (si viene a true es libre).
 * Con estos param, genera la url del proceso de compra añaddiendo los params a la url con los datos del dispositivo
 * @param {Object} offerSelected
 * @param {boolean} type
 * @returns Url del proceso de compra
 */
export const createBuyProcess = ({
  offerSelected,
  type,
  insurance,
  segment,
  categoryFusionInfo,
  clientType
}) => {
  let url = "";
  let onlyClientProcess = false;
  if (type === "libre" || !type) {
    url = URLSPURCHASEPROCESS.processVTL;
    url = `${url}?${BUYPROCESS_URL_KEY_PARAMS.pid}=${BUYPROCESS_URL_PARAM_PID.meInteresaLibre}`;
    url = `${url}&${BUYPROCESS_URL_KEY_PARAMS.buyProcess}=dispositivos`;
    url = `${url}&${BUYPROCESS_URL_KEY_PARAMS.alias}=${offerSelected.productAlias
      }${insurance && insurance.name && insurance.type !== "NS"
        ? `,seguro-movil-${toKebabCase(insurance.name)}`
        : ""
      }`;
  } else if (type === "incluido" || type === "adicional" || type === "renovacion-incluido") {
    const { isSwapOffer } = isMovistarSwapOffer(offerSelected);
    onlyClientProcess = true;
    if (type === "renovacion-incluido") {
      // Semiautomatico si TCOL dice segundo incluido, si no, adicional y a manual
      if (clientType === USUARIO_SEGUNDO_R2R_INCLUIDO) {
        url = `${URLSPURCHASEPROCESS.processRenewR2RSemiautomatic}?dispositivo=incluido`;
      } else {
        url = `${URLSPURCHASEPROCESS.processRenewR2RManual}?dispositivo=incluido`;
      }
    } else {
      if (isPackOffer(offerSelected)) {
        url = `${URLSPURCHASEPROCESS.processPackR2R}?dispositivo=${type}`;
      } else if (clientType === USUARIO_SEGUNDO_R2R_INCLUIDO) {
        url = `${URLSPURCHASEPROCESS.processSecondaryIncludedR2R}?dispositivo=${type}`;
      } else {
        url = `${URLSPURCHASEPROCESS.processR2R}?dispositivo=${type}`;
      }
    }
    url = `${url}&${BUYPROCESS_URL_KEY_PARAMS.alias}=${offerSelected.productAlias
      }-fusion${insurance && insurance.name && insurance.type !== "NS"
        ? `,seguro-movil-${toKebabCase(insurance.name)}`
        : ""
      }`;
    //TODO: Borrar cuando se desdoblen po, y haya productOfferingTypes
    url = `${url}${isSwapOffer ? `&swap=true` : ""}`
  }
  if ((type === 'libre' || type === 'incluido') && !(isPackOffer(offerSelected))) {
    const { monthlyFinalPrice } = getFinalOfferPriceByType({ offer: offerSelected, categoryFusionInfo, type })
    url = `${url}&${BUYPROCESS_URL_KEY_PARAMS.vval}=${monthlyFinalPrice}`;
  }

  url = `${url}&${getMainPurchaseParams(offerSelected)}`;
  url = `${url}${insurance && insurance.tsType ? `&insurance=${insurance.tsType}` : ""}`;
  url = `${url}${insurance && insurance.productId ? `&insurancePo=${insurance.productId}` : ""}`;
  url = `${url}${insurance && insurance.modalityId ? `&insuranceModality=${insurance.modalityId}` : ""}`;
  url = `${url}${segment ? `&segment=${segment}` : ""}`;
  url = `${url}${offerSelected?.offerProfile ? `&profiles=${offerSelected.offerProfile}` : ""}`;
  if (onlyClientProcess) {
    //Se envia al login si es un proceso para ya clientes
    url = goLoginCallbackUrl(url);
  }
  return url;
};

/**
 * La función recibe una oferta, y el tipo de oferta, de modo que se devolverá el precio catalogado para ese tipo.
 * @param {Object} offer incluido|adicional|libre
 * @param {string} categoryFusionInfo av|mv|bv
 * @param {string} type 
 * @returns Precio final de la oferta
 */

export const getFinalOfferPriceByType = ({ offer, categoryFusionInfo, type }) => {
  let monthlyFinalPrice;
  let monthlyPriceBeforeDiscounts;
  let totalAmount;
  let totalAmountBeforeDiscounts;
  let installments;
  let saving;
  let fundingOfferInfo;
  let moreThanOneQuota = false;
  if (!categoryFusionInfo) {
    categoryFusionInfo = "av";
  }
  if (offer) {
    switch (type) {
      case "incluido":
      case "renovacion-incluido":
        installments = "48"
        if (
          offer[`pvp_fusion_${categoryFusionInfo}_promo`] !== "" &&
          offer[`pvp_fusion_${categoryFusionInfo}_promo`] !== "-" &&
          offer[`pvp_fusion_${categoryFusionInfo}_promo`] !== undefined
        ) {
          monthlyFinalPrice = offer[`pvp_fusion_${categoryFusionInfo}_promo`];
        }
        if (
          offer[`pvp_fusion_${categoryFusionInfo}`] !== "" &&
          offer[`pvp_fusion_${categoryFusionInfo}`] !== "-" &&
          offer[`pvp_fusion_${categoryFusionInfo}`] !== undefined
        ) {
          if (monthlyFinalPrice) {
            monthlyPriceBeforeDiscounts = offer[`pvp_fusion_${categoryFusionInfo}`];
          } else {
            monthlyFinalPrice = offer[`pvp_fusion_${categoryFusionInfo}`];
          }
        }
        totalAmount = calculateTotalAmountR2R(monthlyFinalPrice);
        //pvpr
        totalAmountBeforeDiscounts = offer.originalPrice;
        saving = Math.round(offer.originalPrice - totalAmount);
        break;
      case "adicional":
        if (offer[`monthlyPayment48`] > -1) {
          installments = "48";
          monthlyFinalPrice = offer[`monthlyPayment48`];
          totalAmount = calculateTotalAmountR2R(monthlyFinalPrice, 48)
        } else if (offer[`monthlyPayment36`] > -1) {
          installments = "36";
          monthlyFinalPrice = offer[`monthlyPayment36`];
          totalAmount = calculateTotalAmountR2R(monthlyFinalPrice, 36)
        } else {
          installments = "24";
          monthlyFinalPrice = offer[`monthlyPayment24`];
          totalAmount = calculateTotalAmountR2R(monthlyFinalPrice, 24)
        }
        //pvpr -> Ensure that totalAmountBeforeDiscounts is only set if totalAmount is less than pvpr
        totalAmountBeforeDiscounts = totalAmount < offer.originalPrice ? offer.originalPrice : null;
        saving = Math.round((offer.originalPrice || offer.currentPrice) - totalAmount);
        const quotas = [
          offer.monthlyPayment24,
          offer.monthlyPayment36,
          offer.monthlyPayment48,
        ];
        moreThanOneQuota = quotas.filter(quota => quota > -1).length > 1;
        break;
      case "libre":
        totalAmount = offer.currentPrice;
        if (offer.originalPrice > 0 && offer.originalPrice !== offer.currentPrice) {
          totalAmountBeforeDiscounts = offer.originalPrice;
        }
        saving = Math.round(totalAmountBeforeDiscounts - totalAmount);
        if (Array.isArray(offer.fundingOfferInfo) && offer.fundingOfferInfo.length > 0) {
          fundingOfferInfo = offer.fundingOfferInfo.map(elem => ({
            monthlyPayment: parsePrice(elem.monthlyPayment),
            totalAmountToPay: parsePrice(elem.totalAmountToPay),
            totalTaxes: parsePrice(elem.totalTaxes),
            months: elem.months,
            tin: parsePrice(elem.tin),
            tae: parsePrice(elem.tae)
          }));
        }
        break;
      default:
        break;
    }
  }

  const price = {
    monthlyFinalPrice,
    installments,
    totalAmount,
    monthlyPriceBeforeDiscounts,
    totalAmountBeforeDiscounts,
    saving,
    fundingOfferInfo,
    moreThanOneQuota
  }
  return price;
}

const filterOfferCardsByCreditLimit = ({ cards, totalPvp }) => {
  let finalCards = [];
  if (Array.isArray(cards)) {
    finalCards = cards.filter(card => {
      //Si hay totalPvp y se excede el limite, se quita la card
      if (totalPvp === undefined || totalPvp === null) {
        return true;
      } else {
        return !(totalPvp >= 0 && card.pvpTotalUntaxed >= 0 && card.pvpTotalUntaxed > totalPvp)
      }
    })
  }
  return finalCards;
}

//TODO Meter logica de la buybox para decidir las cards a pintar aqui
const addFinalPriceToCards = ({ cards, categoryFusionInfo, type }) =>
  Array.isArray(cards)
    ? cards.map(offer => {
      const {
        totalAmount,
        totalAmountBeforeDiscounts,
        ...finalOfferPrice
      } = getFinalOfferPriceByType({ offer, categoryFusionInfo, type });

      return {
        ...offer,
        ...finalOfferPrice,
        finalPrice: totalAmount,
        finalPriceBeforeDiscounts: totalAmountBeforeDiscounts
      };
    })
    : [];

// ñapa para que si en adicional el pvpr es menor que el precio final, se oculte precio tachado en libre
const removeInvalidFreePriceBeforeDiscounts = ({ cardFree, cardAditionalR2R }) => {
  const additionalPvprLower = cardAditionalR2R.some(
    offer => offer.finalPrice > offer.originalPrice
  );

  if (!additionalPvprLower) {
    return cardFree;
  }

  return cardFree.map(offer => {
    if (offer.finalPriceBeforeDiscounts == null) {
      return offer;
    }

    return {
      ...offer,
      finalPriceBeforeDiscounts: null,
      saving: null
    };
  });
};

const filterCardsByClientType = ({ cards, clientType, totalPvp, categoryFusionInfo }) => {
  let finalCards = {
    cardFree: [],
    cardR2R: [],
    cardAditionalR2R: []
  };
  if (cards) {
    const { cardFree, cardR2R, cardAditionalR2R } = cards;
    finalCards.cardFree = addFinalPriceToCards({
      cards: cardFree,
      categoryFusionInfo,
      type: "libre"
    });

    if (clientType === USUARIO_PRIMER_R2R_INCLUIDO || clientType === USUARIO_SEGUNDO_R2R_INCLUIDO || clientType === USUARIO_R2R_ADICIONAL_CON_RENOVACION) {
      if (Array.isArray(cardR2R)) {
        finalCards.cardR2R = addFinalPriceToCards({
          cards: cardR2R/*filterOfferCardsByCreditLimit({
            cards: cardR2R,
            totalPvp
          })*/,
          categoryFusionInfo,
          type: "incluido"
        })
      }
    }

    if (clientType === USUARIO_R2R_ADICIONAL || clientType === USUARIO_R2R_ADICIONAL_CON_RENOVACION) {
      finalCards.cardAditionalR2R = addFinalPriceToCards({
        cards: cardAditionalR2R/*filterOfferCardsByCreditLimit({
          cards: cardAditionalR2R,
          totalPvp
        })*/,
        categoryFusionInfo,
        type: "adicional"
      })
    }

    if (clientType === USUARIO_NO_LOGADO) {
      finalCards.cardR2R = addFinalPriceToCards({
        cards: cardR2R,
        categoryFusionInfo,
        type: "incluido"
      })
    }

    finalCards.cardFree = removeInvalidFreePriceBeforeDiscounts(finalCards);
  }
  return finalCards;
}

export const getProductOffersByClientType = ({ product, clientType, totalPvp, categoryFusionInfo }) => {
  let clientOffers = {
  };
  if (product.offers) {
    const productOffers = product.offers;
    if (productOffers) {
      const capacities = Object.keys(productOffers);
      capacities.map(capacity => {
        const colors = Object.keys(productOffers[capacity]);
        colors.map(color => {
          const { cards } = productOffers[capacity][color];
          if (cards) {
            clientOffers = {
              ...clientOffers,
              [capacity]: {
                ...clientOffers?.[capacity],
                [color]: {
                  ...productOffers?.[capacity]?.[color],
                  cards: filterCardsByClientType({ cards, clientType, totalPvp, categoryFusionInfo })
                }
              }
            }
          }
        })
      })
    }
  }
  return clientOffers;
}

export const checkIfColorOfferIsEmpty = (offer, clientType, selectedColor, selectedCapacity) => {
  let offersCards = [];
  if (Array.isArray(offer.cards.cardFree)) {
    offersCards.push(...offer.cards.cardFree)
  }
  switch (clientType) {
    case USUARIO_NO_LOGADO:
    case USUARIO_PRIMER_R2R_INCLUIDO:
    case USUARIO_SEGUNDO_R2R_INCLUIDO:
      if (Array.isArray(offer.cards.cardR2R)) {
        offersCards.push(...offer.cards.cardR2R)
      }
      break;
    case USUARIO_R2R_ADICIONAL:
    case USUARIO_R2R_ADICIONAL_CON_RENOVACION:
      if (Array.isArray(offer.cards.cardAditionalR2R)) {
        offersCards.push(...offer.cards.cardAditionalR2R)
      }
      break;
    default:
  }
  if (Array.isArray(offersCards) && offersCards.length > 0) {
    //La oferta seleccionada por url se muestra aunque no tenga stock
    if (offersCards[0].colorHex === selectedColor && offersCards[0].storage === selectedCapacity) {
      return false;
    }
    //En el resto de casos, se mira el stock en prod para ocultarlo
    const hasStockAvailable = offersCards.find(offer => offer.stock !== 0);

    //Hide no stock colors in prod or dev
    if (!hasStockAvailable && (process.env.NEXT_PUBLIC_ENV_NAME === "prod" ||
      process.env.NEXT_PUBLIC_ENV_NAME === "dev_ssr")) {
      return true;
    } else {
      return false;
    }
  }
  return true;
};

export const checkIfCapacityOfferIsEmpty = (offers, capacity, clientType, selectedColor, selectedCapacity) => {
  const colors = Object.keys(offers[capacity]);
  let noOffer = true;
  colors.forEach((color) => {
    if (!checkIfColorOfferIsEmpty(offers[capacity][color], clientType, selectedColor, selectedCapacity)) {
      noOffer = false;
    }
  });
  return noOffer;
};

export const isAvailable = (offers) => {
  let available = false;
  if (offers && offers.cards) {
    const cardTypes = Object.keys(offers.cards);
    available = cardTypes.some(type =>
      Array.isArray(offers.cards[type]) && offers.cards[type].length > 0
    )
  }
  return available;
};

/**
 * Función que devuelve el color asociado a la nueva capacidad seleccionada, teniendo en cuenta el color previo.
 * No siempre será posible mantener la selección de color, y en ese caso se devuelve el por defecto.
 * Se tiene en cuenta el tipo de cliente, y la oferta a mostrar forzada (si la hay)
 * @param {string} newCapacity
 * @param {string} colorProduct
 * @param {Object} offers
 * @param {string} clientType
 * @param {string} offerCategory
 * @returns Nuevo color asociado a la capacidad seleccionada
 */

export const getNewColorBySelectedCapacity = (newCapacity, colorProduct, offers, clientType) => {
  let newColorHex = null;
  const keys = Object.keys(offers[newCapacity]);
  if (
    keys.includes(colorProduct) &&
    !checkIfColorOfferIsEmpty(
      offers[newCapacity][colorProduct],
      clientType
    )
  ) {
    newColorHex = colorProduct;
  } else {
    for (const key of keys) {
      if (
        !checkIfColorOfferIsEmpty(
          offers[newCapacity][key],
          clientType
        )
      ) {
        newColorHex = key;
        break;
      }
    }
  }
  return newColorHex;
}

export const isMovistarSwapOffer = (offer) => {
  let isSwapOffer = false;
  let swapRenewalPeriod;

  if (offer && Array.isArray(offer.tags) && offer.tags.includes("MOVISTAR SWAP")) {
    isSwapOffer = true;
    swapRenewalPeriod = offer.swapRenewalPeriod || 24;
  }
  return {
    isSwapOffer,
    swapRenewalPeriod
  }
};

export const getWarrantyInfo = (productTypes) => {
  const warrantyInfo = {
    warrantyYears: 3,
    type: "standard"
  };
  if (Array.isArray(productTypes)) {
    if (productTypes.includes("reacondicionado-certificado")) {
      warrantyInfo.warrantyYears = 2;
      warrantyInfo.type = "certified-refurbished";
    } else if (productTypes.includes("reacondicionado")) {
      warrantyInfo.warrantyYears = 2;
      warrantyInfo.type = "refurbished";
    }
  }
  return warrantyInfo;
}

export const getFirstSwapOfferInfo = (offers) => {
  let isSwapOffer = false;
  let swapRenewalPeriod;

  const swapOffer = (Array.isArray(offers?.cards?.cardR2R) &&
    offers.cards.cardR2R.find(card => isMovistarSwapOffer(card).isSwapOffer)) ||

    (Array.isArray(offers?.cards?.cardAditionalR2R) &&
      offers.cards.cardAditionalR2R.find(card => isMovistarSwapOffer(card).isSwapOffer)) ||

    (Array.isArray(offers?.cards?.cardFree) &&
      offers.cards.cardFree.find(card => isMovistarSwapOffer(card).isSwapOffer));

  if (swapOffer) {
    isSwapOffer = true;
    swapRenewalPeriod = swapOffer.swapRenewalPeriod || 24;
  }
  return {
    isSwapOffer,
    swapRenewalPeriod
  }
};

export const getLabelPromo = (offers, capacityProduct, colorProduct) => {
  let finalLabelPromo = null;
  if (offers && capacityProduct && colorProduct) {
    finalLabelPromo = offers[capacityProduct][colorProduct].stamp;
    //ñapa rodrigolica de la crazy label para que solo salga si todas las ofertas son CPO
    if (finalLabelPromo?.text === "Certificado Apple") {
      for (const cardType in offers[capacityProduct][colorProduct]?.cards) {
        if (offers[capacityProduct][colorProduct].cards[cardType].some(
          offer => !offer.category.includes("REACONDICIONADO CERTIFICADO")
        )) {
          finalLabelPromo = offers[capacityProduct][colorProduct][cardType]?.labelPromo || "";
          break;
        }
      }
    }
  }
  return finalLabelPromo;
};

export const isPackOffer = (offerSelected) => {
  let isPack = false;
  if (offerSelected) {
    let { productOffering, productSpecs } = offerSelected;
    let { color, storage } = productSpecs;
    isPack = productOffering.includes(":") && color.includes(":") & storage.includes(":");
  }
  return isPack;
}
